import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Alert,
  Button,
  IconButton,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../../services/api';

const EmergencyContactForm = ({ initialData = [], onSave, onCancel, loading = false }) => {
  const [contactList, setContactList] = React.useState(
    initialData.length > 0 ? initialData : [{}]
  );
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    // 只有在数据没有被修改的情况下才更新表单数据
    if (!isDataModified) {
      setContactList(initialData.length > 0 ? initialData : [{}]);
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，当外部触发保存时自动执行保存逻辑
  React.useEffect(() => {
    if (loading) {
      // 使用 setTimeout 确保在下一个事件循环中执行，避免竞态条件
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (index, field, value) => {
    setContactList(prev => {
      const newList = [...prev];
      newList[index] = {
        ...newList[index],
        [field]: value
      };
      return newList;
    });
    
    // 标记数据已被修改
    setIsDataModified(true);
    
    // 清除对应字段的错误
    const errorKey = `contact.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // 添加联系人
  const handleAddContact = () => {
    if (contactList.length < 2) {
      setContactList(prev => [...prev, {}]);
      // 标记数据已被修改
      setIsDataModified(true);
    }
  };

  // 删除联系人
  const handleRemoveContact = (index) => {
    if (contactList.length > 1) {
      setContactList(prev => prev.filter((_, i) => i !== index));
      
      // 标记数据已被修改
      setIsDataModified(true);
      
      // 清除相关错误
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`contact.${index}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    
    contactList.forEach((contact, index) => {
      if (!contact.name) {
        newErrors[`contact.${index}.name`] = '请输入联系人姓名';
      }
      if (!contact.relation) {
        newErrors[`contact.${index}.relation`] = '请输入与申请人关系';
      }
      if (!contact.mobilePhone && !contact.phone && !contact.homePhone) {
        newErrors[`contact.${index}.mobilePhone`] = '请至少填写一个联系电话';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      // 验证失败时，显示错误提示
      setShowErrorSnackbar(true);
      // 通知父组件保存失败
      onSave && onSave(null);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-emergency-contact', {
        emergencyContact: contactList
      });
      
      if (response && response.success) {
        // 保存成功后，重置修改标记
        setIsDataModified(false);
        onSave && onSave(contactList);
      } else {
        setSaveError(response?.message || '保存失败');
        setShowErrorSnackbar(true);
        onSave && onSave(null);
      }
    } catch (error) {
      console.error('保存紧急联系人失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      setShowErrorSnackbar(true);
      onSave && onSave(null);
    }
  };

  // 关闭错误提示
  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  return (
    <Box>
      {/* 移除固定位置的错误提示 */}
      
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        紧急联系人信息
      </Typography>

      {contactList.map((contact, index) => (
        <Card key={index} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                紧急联系人 {index + 1}
              </Typography>
              {contactList.length > 1 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveContact(index)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="姓名 *"
                  value={contact.name || ''}
                  onChange={(e) => handleFormChange(index, 'name', e.target.value)}
                  error={!!errors[`contact.${index}.name`]}
                  helperText={errors[`contact.${index}.name`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="与申请人关系 *"
                  value={contact.relation || ''}
                  onChange={(e) => handleFormChange(index, 'relation', e.target.value)}
                  error={!!errors[`contact.${index}.relation`]}
                  helperText={errors[`contact.${index}.relation`]}
                  placeholder="例如：父亲、母亲、配偶等"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="出生日期"
                  type="date"
                  value={contact.birthDate || ''}
                  onChange={(e) => handleFormChange(index, 'birthDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="身份证号"
                  value={contact.idNumber || ''}
                  onChange={(e) => handleFormChange(index, 'idNumber', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="护照号"
                  value={contact.passportNumber || ''}
                  onChange={(e) => handleFormChange(index, 'passportNumber', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="工作单位名称"
                  value={contact.companyName || ''}
                  onChange={(e) => handleFormChange(index, 'companyName', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="职位"
                  value={contact.position || ''}
                  onChange={(e) => handleFormChange(index, 'position', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="月薪(人民币)"
                  value={contact.salary || ''}
                  onChange={(e) => handleFormChange(index, 'salary', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="手机号码"
                  value={contact.mobilePhone || contact.mobile || ''}
                  onChange={(e) => handleFormChange(index, 'mobilePhone', e.target.value)}
                  error={!!errors[`contact.${index}.mobilePhone`]}
                  helperText={errors[`contact.${index}.mobilePhone`]}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="家庭电话"
                  value={contact.homePhone || ''}
                  onChange={(e) => handleFormChange(index, 'homePhone', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="单位电话"
                  value={contact.companyPhone || contact.workPhone || ''}
                  onChange={(e) => handleFormChange(index, 'companyPhone', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮箱"
                  type="email"
                  value={contact.email || ''}
                  onChange={(e) => handleFormChange(index, 'email', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="联系地址"
                  multiline
                  rows={2}
                  value={contact.address || contact.homeAddress || ''}
                  onChange={(e) => handleFormChange(index, 'address', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="邮政编码"
                  value={contact.postalCode || ''}
                  onChange={(e) => handleFormChange(index, 'postalCode', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddContact}
        disabled={contactList.length >= 2}
        sx={{ mb: 3 }}
      >
        添加紧急联系人 {contactList.length}/2
      </Button>

      {/* 错误提示 Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmergencyContactForm; 